import { Form, Input, Select, InputNumber, Button, Card, Row, Col, notification, Switch } from "antd";
import { useEffect, useState } from "react";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { NomenclatureAutoComplete } from "src/shared";

const { TextArea } = Input;
const { Option } = Select;

export default function TechCardsForm({
  formContext,
  record,
  parent,
  api,
  token,
  setChangeData,
  onSelectNom,
  nomenclatureNames,
}) {
  const [localNomenclatureNames, setLocalNomenclatureNames] = useState(nomenclatureNames || {});

  useEffect(() => {
    console.log("TechCardsForm: Initializing with record:", JSON.stringify(record, null, 2));
    if (record && record.items) {
      const initialNames = { ...nomenclatureNames };
      record.items.forEach((item, index) => {
        if (item.nomenclature_id && !initialNames[index]) {
          initialNames[index] = item.nomenclature_name || item.nomenclature_id;
        }
      });
      setLocalNomenclatureNames(initialNames);
      console.log("TechCardsForm: Initial nomenclature names:", initialNames);
    }
  }, [record, nomenclatureNames]);

  const calculateGrossWeight = (netWeight, coldWaste, heatWaste) => {
    const net = parseFloat(netWeight || 0);
    const cold = parseFloat(coldWaste || 0);
    const heat = parseFloat(heatWaste || 0);
    const baseWeight = net + cold + heat;
    const adjustedWeight = baseWeight < 0.01 ? 0.01 : baseWeight;
    return parseFloat(adjustedWeight.toFixed(3));
  };

  const updateGrossWeight = (index) => {
    const items = formContext.getFieldValue("items") || [];
    const item = items[index];

    if (item) {
      const grossWeight = calculateGrossWeight(
        item.net_weight,
        item.waste_from_cold_processing,
        item.waste_from_heat_processing
      );

      const updatedItems = items.map((itm, idx) =>
        idx === index ? { ...itm, gross_weight: grossWeight } : itm
      );

      formContext.setFieldsValue({ items: updatedItems });
      console.log("TechCardsForm: Updated gross weight for item", index, ":", grossWeight);
    }
  };

  const handleSelectNom = async (val, index) => {
    try {
      console.log("TechCardsForm: Selecting nomenclature:", val, "for index:", index);
      const response = await fetch(`${api}nomenclature/${val}/?token=${token}`);
      if (!response.ok) throw new Error("Failed to fetch nomenclature");

      const data = await response.json();
      console.log("TechCardsForm: Nomenclature data:", data);

      if (!data.id) {
        notification.error({ message: "Ошибка", description: "Номенклатура не найдена" });
        return;
      }

      const items = formContext.getFieldValue("items") || [];
      const updatedItems = [...items];

      if (updatedItems[index]) {
        updatedItems[index] = {
          ...updatedItems[index],
          nomenclature_id: Number(data.id),
          net_weight: data.net_weight || 0.1,
          gross_weight: calculateGrossWeight(
            data.net_weight || 0.1,
            updatedItems[index].waste_from_cold_processing || 0,
            updatedItems[index].waste_from_heat_processing || 0.01
          ),
          output: data.output || 0.1,
        };

        formContext.setFieldsValue({ items: updatedItems });
        setLocalNomenclatureNames((prev) => ({
          ...prev,
          [index]: data.name || "Неизвестно",
        }));
        console.log("TechCardsForm: Updated items after nomenclature select:", updatedItems);

        if (setChangeData) setChangeData(true);
      }

      if (onSelectNom) {
        onSelectNom(val, index);
      }
    } catch (error) {
      console.error("TechCardsForm: Error selecting nomenclature:", error);
      notification.error({
        message: "Ошибка",
        description: "Произошла ошибка при выборе номенклатуры",
      });
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    console.log("TechCardsForm: Form values changed:", JSON.stringify(allValues, null, 2));
    if (setChangeData) setChangeData(true);

    if (changedValues.items) {
      const changedIndex = Object.keys(changedValues.items)[0];
      const changedField = Object.keys(changedValues.items[changedIndex])[0];

      if (
        changedField &&
        (changedField === "net_weight" ||
          changedField === "waste_from_cold_processing" ||
          changedField === "waste_from_heat_processing")
      ) {
        updateGrossWeight(parseInt(changedIndex));
      }
    }
  };

  const initializeItemWeights = (items) => {
    return items.map((item) => ({
      ...item,
      gross_weight: calculateGrossWeight(
        item.net_weight || 0.1,
        item.waste_from_cold_processing || 0,
        item.waste_from_heat_processing || 0.01
      ),
    }));
  };

  return (
    <Form
      form={formContext}
      initialValues={{
        card_type: "reference",
        auto_produce: false,
        status: record?.status === "active" || false,
        ...record,
        items: record?.items ? initializeItemWeights(record.items) : [],
      }}
      layout="vertical"
      onValuesChange={handleValuesChange}
    >
      <Card size="small" title="Основная информация" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Название"
              name="name"
              rules={[{ required: true, message: "Введите название техкарты" }]}
            >
              <Input placeholder="Введите название техкарты" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Тип карты"
              name="card_type"
              rules={[{ required: true, message: "Выберите тип карты" }]}
            >
              <Select>
                <Option value="reference">Эталонная</Option>
                <Option value="automatic">Автоматическая</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Код" name="code">
              <Input placeholder="Введите код" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Родительская карта" name="parent">
              <Select
                options={parent}
                placeholder="Выберите родительскую техкарту"
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Статус" name="status" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Описание" name="description">
          <TextArea
            rows={3}
            placeholder="Введите описание техкарты"
            style={{ resize: "vertical" }}
          />
        </Form.Item>

        <Form.Item name="auto_produce" hidden>
          <Input type="hidden" />
        </Form.Item>
      </Card>

      <Form.List name="items">
        {(fields, { add, remove }) => (
          <Card
            size="small"
            title={
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <span>Состав техкарты</span>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => {
                    add({
                      nomenclature_id: null,
                      type_of_processing: "cutting",
                      net_weight: 0.1,
                      waste_from_cold_processing: 0,
                      waste_from_heat_processing: 0.01,
                      quantity: 1,
                      gross_weight: 0.11,
                      output: 0.1,
                    });
                    if (setChangeData) setChangeData(true);
                  }}
                  type="primary"
                  size="small"
                >
                  Добавить позицию
                </Button>
              </div>
            }
          >
            {fields.map((field, index) => {
            const itemName = formContext.getFieldValue(["items", index, "nomenclature_name"]) || localNomenclatureNames[index] || "";
            const displayName = itemName || "";
            console.log(`TechCardsForm: Index ${index} - itemName: ${itemName}, localNomenclatureNames[${index}]: ${localNomenclatureNames[index]}, displayName: ${displayName}`);

              return (
                <Card
                  key={field.key}
                  size="small"
                  style={{ marginBottom: 12 }}
                  title={`Позиция ${index + 1}`}
                  extra={
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        remove(field.name);
                        setLocalNomenclatureNames((prev) => {
                          const newNames = { ...prev };
                          delete newNames[index];
                          return newNames;
                        });
                        if (setChangeData) setChangeData(true);
                      }}
                      danger
                      size="small"
                    >
                      Удалить
                    </Button>
                  }
                >
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item
                        label="Номенклатура"
                        name={[field.name, "nomenclature_id"]}
                        rules={[{ required: true, message: "Выберите номенклатуру" }]}
                      >
                        <Row gutter={0} align="middle" style={{ width: "100%" }}>
                          <Col flex="15%">
                            <NomenclatureAutoComplete
                              buttonIntegrated={true}
                              api={api}
                              token={token}
                              onSelect={(val) => handleSelectNom(val, index)}
                              selectedId={formContext.getFieldValue(["items", index, "nomenclature_id"])}
                              style={{ width: "100%" }}
                            />
                          </Col>
                          <Col flex="85%" className="custom-col-width">
                            <Input
                              value={displayName}
                              disabled
                              placeholder="Выберите номенклатуру"
                              style={{
                                width: "100%",
                                borderRadius: "0 10px 10px 0",
                                background: "white",
                                color: "black",
                              }}
                            />
                          </Col>
                        </Row>
                      </Form.Item>
                    </Col>

                    <Row gutter={16}>
                      <Col xs={12} lg={4}>
                        <Form.Item
                          label="Тип обработки"
                          name={[field.name, "type_of_processing"]}
                          rules={[{ required: true, message: "Выберите тип обработки" }]}
                        >
                          <Select style={{ width: "100%" }}>
                            <Option value="cutting">Нарезка</Option>
                            <Option value="frying">Жарка</Option>
                            <Option value="boiling">Варка</Option>
                            <Option value="baking">Запекание</Option>
                            <Option value="mixing">Смешивание</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={12} lg={4}>
                        <Form.Item
                          label="Количество"
                          name={[field.name, "quantity"]}
                          rules={[{ required: true, message: "Введите количество" }, { type: "number", min: 1, message: "Значение должно быть больше 0" }]}
                        >
                          <InputNumber min={1} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col xs={12} lg={4}>
                        <Form.Item
                          label="Вес нетто (кг)"
                          name={[field.name, "net_weight"]}
                          rules={[{ required: true, message: "Введите вес нетто" }, { type: "number", min: 0.1, message: "Значение должно быть больше 0.1" }]}
                        >
                          <InputNumber
                            min={0.1}
                            step={0.1}
                            style={{ width: "100%" }}
                            onChange={() => updateGrossWeight(index)}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} lg={4}>
                        <Form.Item
                          label="Отх. хол. (кг)"
                          name={[field.name, "waste_from_cold_processing"]}
                          rules={[{ required: true, message: "Введите потери при холодной обработке" }, { type: "number", min: 0, message: "Значение не может быть отрицательным" }]}
                        >
                          <InputNumber
                            min={0}
                            step={0.01}
                            style={{ width: "100%" }}
                            onChange={() => updateGrossWeight(index)}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} lg={4}>
                        <Form.Item
                          label="Отх. тепл. (кг)"
                          name={[field.name, "waste_from_heat_processing"]}
                          rules={[{ required: true, message: "Введите значение больше 0" }, { type: "number", min: 0.01, message: "Значение должно быть больше 0" }]}
                        >
                          <InputNumber
                            min={0.01}
                            step={0.01}
                            style={{ width: "100%" }}
                            onChange={() => updateGrossWeight(index)}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} lg={4}>
                        <Form.Item
                          label="Выход (кг)"
                          name={[field.name, "output"]}
                          rules={[{ required: true, message: "Введите выход" }, { type: "number", min: 0.01, message: "Значение должно быть больше 0" }]}
                        >
                          <InputNumber
                            min={0.01}
                            step={0.01}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={4}>
                        <Form.Item
                          label="Вес брутто (кг)"
                          name={[field.name, "gross_weight"]}
                          rules={[{ required: true, message: "Вес брутто обязателен" }, { type: "number", min: 0.01, message: "Значение должно быть больше 0" }]}
                        >
                          <InputNumber
                            disabled
                            style={{ width: "100%", fontWeight: "bold" }}
                            formatter={(value) => `${value} кг`}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Row>
                </Card>
              );
            })}

            {fields.length === 0 && (
              <div style={{ textAlign: "center", padding: 20, color: "#999" }}>
                Нет добавленных позиций. Нажмите "Добавить позицию", чтобы начать.
              </div>
            )}
          </Card>
        )}
      </Form.List>
    </Form>
  );
}